import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
	Alarm,
	Controller,
	ControllerMetricsResponse,
	MeterSetting,
	NotificationSetting,
	Place,
	Price,
	Tenant,
	Update,
	User
} from '../../types'

type ControllersResponse = Controller[]
type TenantsResponse = Tenant[]
type UsersResponse = User[]
type AlarmsResponse = Alarm[]
type NotificationSettingsResponse = NotificationSetting[]
type MeterSettingsResponse = MeterSetting[]

// Define a service using a base URL and expected endpoints
export const api = createApi({
	reducerPath: 'api',
	baseQuery: fetchBaseQuery({ baseUrl: '/api/' }),
	tagTypes: ['Controllers', 'ControllersAdmin', 'Tenants', 'UsersAdmin', "Settings", "Users", "NotificationSettings", "EnergyPrices", "MeterSettings", "Updates"],
	endpoints: (build) => ({
		getControllerCurrentMetrics: build.query<ControllerMetricsResponse, string>({
			query: (id) => `controllers-v1/${id}/lastmetrics-v1`,
		}),
		getControllerMetrics: build.query<any, any>({
			query: (data) => {
				const { id, start, stop } = data;
				return {
					url: `controllers-v1/${id}/metrics-v1?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}`
				}
			},
		}),
		getConsumptionStatistics: build.query<any, any>({
			query: (data) => {
				const { start, stop, groupby, controllerIds, position } = data;
				return {
					url: `/statistics/consumption-v1?start=${encodeURIComponent(start)}&stop=${encodeURIComponent(stop)}&groupby=${groupby}&controllerIds=${controllerIds}&position=${position}`
				}
			},
		}),
		getController: build.query<Controller, string>({
			query: (id) => `controllers-v1/${id}`,
			providesTags: (_result, _err, id) => [{ type: 'Controllers', id }],
		}),
		getControllers: build.query<ControllersResponse, void>({
			query: () => ({ url: 'controllers-v1' }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'Controllers' as const, id })),
						{ type: 'Controllers', id: 'LIST' },
					]
					: [{ type: 'Controllers', id: 'LIST' }],
		}),
		getControllerAlarms: build.query<AlarmsResponse, string>({
			query: (id) => `controllers-v1/${id}/alarms-v1`,
		}),
		// NotificationSettings
		getControllerNotificationSettings: build.query<NotificationSettingsResponse, string>({
			query: (id) => `controllers-v1/${id}/notificationsettings-v1`,
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'NotificationSettings' as const, id })),
						{ type: 'NotificationSettings', id: 'LIST' },
					]
					: [{ type: 'NotificationSettings', id: 'LIST' }],
		}),
		addControllerNotificationSetting: build.mutation<NotificationSetting, NotificationSetting>({
			query(body) {
				return {
					url: `controllers-v1/${body.controllerId}/notificationsettings-v1`,
					method: 'POST',
					body,
				}
			},
			invalidatesTags: [{ type: 'NotificationSettings', id: 'LIST' }],
		}),
		deleteControllerNotificationSetting: build.mutation<NotificationSetting, NotificationSetting>({
			query(body) {
				return {
					url: `controllers-v1/${body.controllerId}/notificationsettings-v1/${body.id}`,
					method: 'DELETE',
				}
			},
			invalidatesTags: [{ type: 'NotificationSettings', id: 'LIST' }],
		}),
		updateControllerNotificationSetting: build.mutation<NotificationSetting, Partial<NotificationSetting>>({
			query(data) {
				const { ...body } = data
				return {
					url: `controllers-v1/${body.controllerId}/notificationsettings-v1/${body.id}`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'NotificationSettings', id }],
		}),
		// MeterSettings
		getControllerMeterSettings: build.query<MeterSettingsResponse, string>({
			query: (id) => `controllers-v1/${id}/metersettings-v1`,
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'MeterSettings' as const, id })),
						{ type: 'MeterSettings', id: 'LIST' },
					]
					: [{ type: 'MeterSettings', id: 'LIST' }],
		}),
		addControllerMeterSetting: build.mutation<MeterSetting, MeterSetting>({
			query(body) {
				return {
					url: `controllers-v1/${body.controllerId}/metersettings-v1`,
					method: 'POST',
					body,
				}
			},
			invalidatesTags: [{ type: 'MeterSettings', id: 'LIST' }],
		}),
		deleteControllerMeterSetting: build.mutation<MeterSetting, MeterSetting>({
			query(body) {
				return {
					url: `controllers-v1/${body.controllerId}/metersettings-v1/${body.id}`,
					method: 'DELETE',
				}
			},
			invalidatesTags: [{ type: 'MeterSettings', id: 'LIST' }],
		}),
		updateControllerMeterSetting: build.mutation<MeterSetting, Partial<MeterSetting>>({
			query(data) {
				const { ...body } = data
				return {
					url: `controllers-v1/${body.controllerId}/metersettings-v1/${body.id}`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'MeterSettings', id }],
		}),
		addController: build.mutation<Controller, Partial<Controller>>({
			query(body) {
				return {
					url: `controllers-v1`,
					method: 'POST',
					body,
				}
			},
			invalidatesTags: [{ type: 'Controllers', id: 'LIST' }],
		}),
		updateController: build.mutation<Controller, Partial<Controller>>({
			query(data) {
				const { id, ...body } = data
				return {
					url: `controllers-v1/${id}`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'Controllers', id }],
		}),
		getControllerAdmin: build.query<Controller, string>({
			query: (id) => `admin/controllers-v1/${id}`,
			providesTags: (_result, _err, id) => [{ type: 'ControllersAdmin', id }],
		}),
		getControllersAdmin: build.query<ControllersResponse, void>({
			query: () => ({ url: 'admin/controllers-v1' }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'ControllersAdmin' as const, id })),
						{ type: 'ControllersAdmin', id: 'LIST' },
					]
					: [{ type: 'ControllersAdmin', id: 'LIST' }],
		}),
		addControllerAdmin: build.mutation<Controller, Partial<Controller>>({
			query(body) {
				return {
					url: `admin/controllers-v1`,
					method: 'POST',
					body,
				}
			},
			invalidatesTags: [{ type: 'ControllersAdmin', id: 'LIST' }],
		}),
		updateControllerAdmin: build.mutation<Controller, Partial<Controller>>({
			query(data) {
				const { id, ...body } = data
				return {
					url: `admin/controllers-v1/${id}`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'ControllersAdmin', id }],
		}),
		getTenant: build.query<Tenant, string>({
			query: (id) => `tenants-v1/${id}`,
			providesTags: (_result, _err, id) => [{ type: 'Tenants', id }],
		}),
		getTenants: build.query<TenantsResponse, void>({
			query: () => ({ url: 'tenants-v1' }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'Tenants' as const, id })),
						{ type: 'Tenants', id: 'LIST' },
					]
					: [{ type: 'Tenants', id: 'LIST' }],
		}),
		addTenant: build.mutation<Tenant, Partial<Tenant>>({
			query(body) {
				return {
					url: `tenants-v1`,
					method: 'POST',
					body,
				}
			},
			invalidatesTags: [{ type: 'Tenants', id: 'LIST' }],
		}),
		updateTenant: build.mutation<Tenant, Partial<Tenant>>({
			query(data) {
				const { id, ...body } = data
				return {
					url: `tenants-v1/${id}`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'Tenants', id }],
		}),

		getUserAdmin: build.query<User, string>({
			query: (id) => `admin/users-v1/${id}`,
			providesTags: (_result, _err, id) => [{ type: 'UsersAdmin', id }],
		}),
		getUsersAdmin: build.query<UsersResponse, void>({
			query: () => ({ url: 'admin/users-v1' }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'UsersAdmin' as const, id })),
						{ type: 'UsersAdmin', id: 'LIST' },
					]
					: [{ type: 'UsersAdmin', id: 'LIST' }],
		}),
		getUsersAdminByTenantId: build.query<UsersResponse, string>({
			query: (tenantId) => ({ url: `admin/users-v1/?tenantId=${tenantId}` }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'UsersAdmin' as const, id })),
						{ type: 'UsersAdmin', id: 'LIST' },
					]
					: [{ type: 'UsersAdmin', id: 'LIST' }],
		}),
		addUserAdmin: build.mutation<User, Partial<User>>({
			query(body) {
				return {
					url: `admin/users-v1`,
					method: 'POST',
					body,
				}
			},
			invalidatesTags: [{ type: 'UsersAdmin', id: 'LIST' }],
		}),
		updateUserAdmin: build.mutation<User, Partial<User>>({
			query(data) {
				const { id, ...body } = data
				return {
					url: `admin/users-v1/${id}`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'UsersAdmin', id }],
		}),
		getUsers: build.query<UsersResponse, void>({
			query: () => ({ url: 'users-v1' }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'Users' as const, id })),
						{ type: 'Users', id: 'LIST' },
					]
					: [{ type: 'Users', id: 'LIST' }],
		}),
		getSettings: build.query<Tenant, void>({
			query: () => `settings-v1`,
			providesTags: [{ type: 'Settings' }],
		}),
		updateSettings: build.mutation<Tenant, Partial<Tenant>>({
			query(data) {
				const { ...body } = data
				return {
					url: `settings-v1`,
					method: 'PUT',
					body,
				}
			},
			invalidatesTags: [{ type: 'Settings' }],
		}),
		getEnergyPrices: build.query<Price[], void>({
			query: () => `energy/prices-v1`,
			providesTags: [{ type: 'EnergyPrices' }],
		}),
		getPlaces: build.query<Place[], void>({
			query: () => `places-v1`,
			keepUnusedDataFor: 3600, // static data, no need to refetch often
		}),

		getUpdate: build.query<Update, string>({
			query: (id) => `admin/updates-v1/${id}`,
			providesTags: (_result, _err, id) => [{ type: 'Updates', id }],
		}),
		getUpdates: build.query<Update[], void>({
			query: () => ({ url: 'admin/updates-v1' }),
			providesTags: (result) =>
				result
					? [
						...result.map(({ id }) => ({ type: 'Updates' as const, id })),
						{ type: 'Updates', id: 'LIST' },
					]
					: [{ type: 'Updates', id: 'LIST' }],
		}),
		deleteUpdate: build.mutation<Update, string>({
			query(id) {
				return {
					url: `admin/updates-v1/${id}`,
					method: 'DELETE',
				}
			},
			// Invalidates all queries that subscribe to this Post `id` only.
			invalidatesTags: (result, error, id) => [{ type: 'Updates', id }],
		}),
		upgradeControllers: build.mutation<void, {id:string, controllers: string[]}>({
			query({id, controllers}) {
				return {
					url: `admin/updates-v1/${id}/assign`,
					method: 'POST',
					body: controllers,
				}
			},
			// invalidatesTags: [{ type: 'Updates', id: 'LIST' }],
		}),
	}),
})

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
	useGetControllerCurrentMetricsQuery,
	useGetControllerMetricsQuery,
	useGetConsumptionStatisticsQuery,
	useGetControllerAlarmsQuery,
	useGetControllerNotificationSettingsQuery,
	useAddControllerNotificationSettingMutation,
	useDeleteControllerNotificationSettingMutation,
	useUpdateControllerNotificationSettingMutation,
	useGetControllerMeterSettingsQuery,
	useAddControllerMeterSettingMutation,
	useDeleteControllerMeterSettingMutation,
	useUpdateControllerMeterSettingMutation,
	useGetControllersQuery,
	useGetControllerQuery,
	useAddControllerMutation,
	useUpdateControllerMutation,
	useGetTenantQuery,
	useGetTenantsQuery,
	useAddTenantMutation,
	useUpdateTenantMutation,
	useGetUserAdminQuery,
	useGetUsersAdminQuery,
	useGetUsersAdminByTenantIdQuery,
	useAddUserAdminMutation,
	useUpdateUserAdminMutation,
	useGetUsersQuery,
	useGetSettingsQuery,
	useUpdateSettingsMutation,
	useGetEnergyPricesQuery,
	useGetPlacesQuery,
	useGetControllersAdminQuery,
	useGetControllerAdminQuery,
	useAddControllerAdminMutation,
	useUpdateControllerAdminMutation,
	useGetUpdateQuery,
	useGetUpdatesQuery,
	useDeleteUpdateMutation,
	useUpgradeControllersMutation,
} = api


//TODO move to app/services/api folder
